import { z } from 'zod';
import { paginationQuery } from './common.js';
import { emailSchema, passwordSchema } from './auth.js';
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/**
 * A member IS an app account — every member has these core pieces of data:
 * name, login (e-mail + password), an app-wide role, and two optional
 * assignments (team, training group), plus a small, deliberate set of
 * optional exceptions (gender, birth date, emergency phone — see
 * `memberWritable` below). Nothing else. Deliberately minimal — see
 * docs/members.md.
 */
export const MEMBER_ROLE_KEYS = ['member', 'club_admin'];
export const memberRoleKey = z.enum(MEMBER_ROLE_KEYS);
export const MEMBER_ROLE_LABELS = {
    member: 'Mitglied',
    club_admin: 'Vereinsadministrator',
};
/** Not a stored field — derived purely from the relational shape (does this
 * member have their own login, and are they linked to anyone via
 * `member_guardians`?), so it's always in sync with reality regardless of
 * how the member was created (self-registration or admin form):
 * - `player` — has an own account, not a guardian for anyone.
 * - `parent` — has an own account AND is a guardian for at least one other
 *   member (registered as "Elternkonto", or added as a guardian by an
 *   admin — either way).
 * - `dependent` — no account of their own, registered as the child of a
 *   "Elternkonto" (or added as a minor by an admin) — someone else
 *   (`member_guardians`) acts for them.
 * `null` only for a legacy member with neither (from before an account was
 * mandatory, and never linked as anyone's guardian relationship). */
export const MEMBER_ACCOUNT_KINDS = ['player', 'parent', 'dependent'];
export const MEMBER_ACCOUNT_KIND_LABELS = {
    player: 'Spieler',
    parent: 'Elternteil',
    dependent: 'Kind',
};
/** Badminton-binär, für die Disziplin-Zulassung der Mannschaftsaufstellung
 * (siehe lineup-rules.ts) — optional, ein Mitglied ohne gesetztes Geschlecht
 * bleibt für jede Disziplin auswählbar. */
export const MEMBER_GENDERS = ['m', 'w'];
export const memberGender = z.enum(MEMBER_GENDERS);
export const MEMBER_GENDER_LABELS = { m: 'Männlich', w: 'Weiblich' };
export const memberWritable = z.object({
    firstName: z.string().trim().min(1).max(120),
    lastName: z.string().trim().min(1).max(120),
    gender: memberGender.nullable().optional(),
    /** Optional, auf Wunsch des Vereins ergänzt (Migration
     * `0038_member_birthdate_emergency_phone.sql`) — nicht Teil der ursprünglich
     * radikal minimalen Stammdaten, siehe docs/members.md. */
    birthDate: isoDate.nullable().optional(),
    /** Freitext-Telefonnummer für den Notfall, keine Formatprüfung über
     * "ist eine plausible Telefonnummer" hinaus. */
    emergencyPhone: z.string().trim().max(40).nullable().optional(),
    /** Ein Elternteil, das (auch) selbst mitspielt/mittrainiert — Default
     * false. Nur relevant, wenn diesem Mitglied mind. ein Kind zugeordnet ist
     * (member_guardians); steuert dann, ob es zusätzlich zu den Kindern selbst
     * als Ziel für Abstimmungen angeboten wird. Siehe docs/members.md. */
    selfRegistrationEnabled: z.boolean().optional(),
});
/** PATCH /members/me — self-service "Konto bearbeiten" (ProfileMenu.tsx):
 * a member edits their own Stammdaten. Deliberately only the plain
 * Stammdaten fields — NOT `selfRegistrationEnabled`
 * (that has its own dedicated, narrower self-service toggle) and never
 * e-mail/password/roleKey, which live in a separate "Kontozugang" area on
 * the admin-facing `MemberDetailPage.tsx` and are only ever admin-editable,
 * by design — see docs/members.md. */
export const updateMineRequest = z.object({
    firstName: z.string().trim().min(1).max(120),
    lastName: z.string().trim().min(1).max(120),
    gender: memberGender.nullable().optional(),
    birthDate: isoDate.nullable().optional(),
    emergencyPhone: z.string().trim().max(40).nullable().optional(),
});
export const setTeamManagerTeamsRequest = z.object({
    teamIds: z.array(z.string().uuid()).max(100),
});
/** PATCH /members/me/self-registration — self-service equivalent of the
 * admin-editable `selfRegistrationEnabled` above, scoped to the caller's
 * own member record only. */
export const setSelfRegistrationRequest = z.object({
    enabled: z.boolean(),
});
/** All five Pflichtfelder are required at creation — a member always starts
 * as a full, working app account. */
export const createMemberRequest = memberWritable.extend({
    email: emailSchema,
    password: passwordSchema,
    roleKey: memberRoleKey,
});
/** On edit, everything stays optional: leaving `password` blank keeps the
 * current one, `email`/`roleKey` only change what's actually sent. */
export const updateMemberRequest = memberWritable.partial().extend({
    email: emailSchema.optional(),
    password: passwordSchema.optional(),
    roleKey: memberRoleKey.optional(),
});
export const memberListQuery = paginationQuery.extend({
    teamId: z.string().uuid().optional(),
    trainingGroupId: z.string().uuid().optional(),
    roleKey: memberRoleKey.optional(),
    /** Spalten-Filter auf der Mitgliederliste (§ Nutzeranfrage „ein Filter für
     * die Spalten, z. B. nur 3. Mannschaft anzeigen") — `accountKind` ist nicht
     * gespeichert (siehe `MemberAccountKind` oben), daher im Service über
     * dieselbe Ableitung wie `accountKindsByMember()` gefiltert, nicht über
     * eine einfache Spalten-Gleichheit. */
    accountKind: z.enum(MEMBER_ACCOUNT_KINDS).optional(),
});
/** PATCH /members/bulk — "mehrere Mitglieder gemeinsam bearbeiten" auf der
 * Mitgliederliste (`MembersPage.tsx`): dieselben Felder wie beim
 * Einzel-Edit, aber bewusst OHNE `email`/`password` — für mehrere Mitglieder
 * dieselbe E-Mail/dasselbe Passwort zu setzen ist nie sinnvoll (E-Mail ist
 * pro Konto eindeutig, ein geteiltes Passwort wäre ein Sicherheitsrisiko).
 * Mannschafts-/Trainingsgruppenzuordnung ist ebenfalls ausgenommen — das
 * sind eigene m:n-Relationen mit eigener, reichhaltigerer Zuordnungs-UI auf
 * `MemberDetailPage.tsx`, kein einfaches Feld wie die übrigen hier. */
export const bulkUpdateMembersRequest = z.object({
    memberIds: z.array(z.string().uuid()).min(1).max(500),
    patch: memberWritable.partial().extend({ roleKey: memberRoleKey.optional() }),
});
/** POST /members/:id/approve — plain approval when both fields are omitted
 * (the common case). Either merge option instead of creating a new record —
 * see `MembersService.approve()`/`mergeSelfIntoExisting()` and
 * docs/members.md. Only ever meaningful together with the
 * `possibleDuplicate` hint `listPendingApproval()` returned for this exact
 * row/child — the server re-validates regardless of what the client sends. */
export const approveMemberRequest = z
    .object({
    /** Fall B: attach this registrant's freshly created account to an
     * existing kontolos dependent member instead of keeping the just-created
     * row — see docs/members.md. */
    mergeSelfWithMemberId: z.string().uuid().optional(),
    /** Fall A: link the parent as an additional guardian of this existing
     * account-holding member instead of materializing the staged child as a
     * new dependent. Applies to the (currently: at most one) staged child —
     * same "one child per registration for now" limitation as
     * `pendingChildRegistrations` itself. */
    mergeChildWithMemberId: z.string().uuid().optional(),
})
    .default({});
//# sourceMappingURL=member.js.map